import React from 'react';
import { bffMutation, bffQuery } from '@parsina/framework/clinical';

/**
 * The کد نظام پزشکی lookup and the practitioner write, shared by the two places
 * that ask for the number: «خوش آمدید!» on «شروع» (an Iranian account's whole
 * registration) and the wizard's «مشخصات پزشک» step (`?doctor=1`).
 *
 * Both talk to the DOCTOR BFF through the clinical transport
 * (lib/clinicalHost.js), and both read their words from the clinical catalogue —
 * the caller passes that catalogue's `t`, and mounts `<ClinicalI18n>` around it.
 */

export const LOOKUP_COUNCIL_MEMBER = `
  query LookupCouncilMember($code: String!) {
    lookupCouncilMember(code: $code) {
      status
      member { councilNumber fullName specialty degree memberType province city }
    }
  }
`;

const COMPLETE_STEP1 = `
  mutation CompleteOnboardingStep1($input: OnboardingStep1Input!) {
    completeOnboardingStep1(input: $input) {
      id fullName medicalCode specialty bio
    }
  }
`;

// ── When the استعلام runs ────────────────────────────────────────────────────
//
// There is no «استعلام» button: the lookup starts by itself, off the number of
// DIGITS typed. The council allows ten searches per ten minutes to the whole
// platform — every pod leaves through one address — so a lookup per keystroke
// would spend that allowance before the first doctor finished typing.
//
//   • 6+ digits — a current council number is six digits, so the moment it is
//     complete there is nothing to wait for;
//   • 4–5 digits — real, but held by long-registered doctors, so those wait for
//     a pause in typing instead;
//   • fewer — never automatically. Leaving the field still looks up.
const IMMEDIATE_DIGITS = 6;
const AUTO_MIN_DIGITS = 4;
const TYPING_PAUSE_MS = 1200;

/**
 * The digits in what was typed, Persian and Arabic-Indic ones included — a
 * phone keyboard produces «۱۲۵۰۰۰», and that is six digits, not zero. Letters
 * of a profession prefix («د-»، «ع-آ-») are not digits and do not count.
 */
export const councilDigits = (value) =>
  String(value ?? '')
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, '');

/**
 * The field's state and its two handlers.
 *
 * @param {(key: string) => string} t the CLINICAL catalogue's `t`
 * @returns {{
 *   code: string, member: object|null, looking: boolean, error: string|null,
 *   retryable: boolean, onChange: (value: string) => void, onBlur: () => void,
 *   retry: () => void,
 * }}
 *   `retryable` is true when the failure was the registry's, not the number's —
 *   the one answer that must never be worded as «کد اشتباه است».
 */
export function useCouncilLookup(t) {
  const [code, setCode] = React.useState('');
  const [member, setMember] = React.useState(null);
  const [looking, setLooking] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [retryable, setRetryable] = React.useState(false);

  // The code the card on screen belongs to. Without it, editing the number
  // after a successful lookup would leave the previous doctor's name sitting
  // under a different number — and «ادامه» would still be enabled.
  const shownFor = React.useRef('');
  // The value of the field right now, readable from a timer callback that was
  // scheduled one or more keystrokes ago.
  const codeRef = React.useRef('');
  // The value the last request was sent for — set BEFORE the await, so it also
  // means "in flight" and one number is never asked twice.
  const askedRef = React.useRef('');
  // …except when the answer was UNAVAILABLE, which is ours and transient: a
  // blur must be able to try that same number again.
  const retryableRef = React.useRef(false);
  // Latest-wins. A doctor who types «12500», pauses long enough for it to be
  // sent, then adds a «0» must never be shown the record for «12500» because
  // it came back second.
  const seqRef = React.useRef(0);
  const pauseRef = React.useRef(null);

  React.useEffect(() => () => clearTimeout(pauseRef.current), []);

  const runLookup = async (raw) => {
    const asked = String(raw ?? '').trim();
    if (!asked || !councilDigits(asked)) return;
    if (asked === askedRef.current && !retryableRef.current) return;

    askedRef.current = asked;
    retryableRef.current = false;
    const seq = ++seqRef.current;
    setLooking(true);
    setError(null);
    setRetryable(false);
    setMember(null);

    // True while this answer is still the one being waited for. A superseded
    // request returns without touching the screen at all — including `looking`,
    // which the newer request owns.
    const current = () => seq === seqRef.current && codeRef.current.trim() === asked;

    try {
      const res = await bffQuery(LOOKUP_COUNCIL_MEMBER, { code: asked });
      if (!current()) return;
      const result = res?.lookupCouncilMember;
      shownFor.current = asked;
      if (result?.status === 'FOUND' && result.member) {
        setMember(result.member);
      } else if (result?.status === 'AMBIGUOUS') {
        setError(t('auth.onboarding.codeAmbiguous'));
      } else if (result?.status === 'NOT_FOUND') {
        setError(t('auth.onboarding.codeNotFound'));
      } else {
        // UNAVAILABLE — ours to fix, not the doctor's. Never «کد اشتباه است».
        retryableRef.current = true;
        setRetryable(true);
        setError(t('auth.onboarding.registryUnavailable'));
      }
      setLooking(false);
    } catch (e) {
      if (!current()) return;
      shownFor.current = '';
      retryableRef.current = true;
      setRetryable(true);
      setError(e.message || t('auth.onboarding.registryUnavailable'));
      setLooking(false);
    }
  };

  const onChange = (value) => {
    setCode(value);
    codeRef.current = value;
    clearTimeout(pauseRef.current);

    if (value.trim() !== shownFor.current) {
      setMember(null);
      setError(null);
      setRetryable(false);
    }

    const digits = councilDigits(value);
    if (digits.length >= IMMEDIATE_DIGITS) {
      runLookup(value);
    } else if (digits.length >= AUTO_MIN_DIGITS) {
      pauseRef.current = setTimeout(() => runLookup(codeRef.current), TYPING_PAUSE_MS);
    }
  };

  // Leaving the field asks for whatever is in it, however short — the one way a
  // doctor with a two- or three-digit number gets a lookup at all.
  const onBlur = () => {
    clearTimeout(pauseRef.current);
    runLookup(codeRef.current);
  };

  const retry = () => { runLookup(codeRef.current); };

  return { code, member, looking, error, setError, retryable, onChange, onBlur, retry };
}

/**
 * Write the practitioner record for a looked-up member. The council's own
 * spelling of the number goes on the wire, never what was typed — and the BFF
 * re-resolves it whatever is sent, so the name cannot be edited from here.
 *
 * @returns {Promise<object|null>} the practitioner, or null when the BFF said nothing
 */
export async function completeCouncilStep(member, { bio } = {}) {
  const res = await bffMutation(COMPLETE_STEP1, {
    input: {
      medicalCode: member.councilNumber,
      bio: String(bio || '').trim() || undefined,
    },
  });
  return res?.completeOnboardingStep1 || null;
}

/** What a refused `completeOnboardingStep1` means, in the clinical catalogue's words. */
export function councilStepError(err, t) {
  const failed = err?.extensions?.code;
  if (failed === 'DUPLICATE_MEDICAL_CODE') return t('auth.onboarding.codeTaken');
  if (failed === 'COUNCIL_CODE_NOT_FOUND') return t('auth.onboarding.codeNotFound');
  if (failed === 'COUNCIL_CODE_AMBIGUOUS') return t('auth.onboarding.codeAmbiguous');
  if (failed === 'COUNCIL_REGISTRY_UNAVAILABLE') return t('auth.onboarding.registryUnavailable');
  return err?.message || t('errors.save.generic');
}
